import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import transformResponse from "./JsonTransformResponse";
import transformRequest from "./JsonTransformRequest";
import { Api, ApiRequestSettings } from "@/custolink-transpiled_by_server/api";

axios.defaults.withCredentials = true;

let i18n_resolved_language = "en";
let i18n_resolved_language_from_browser = "en";

export function setLanguageHeaders(
  i18nResolvedLanguageForUiText: string | null,
  i18nResolvedLanguageFromBrowser: string | null,
) {
  // yes, doing it this is not nice. It should be kind of a hook/interceptor instead.
  if (i18nResolvedLanguageForUiText != null) {
    i18n_resolved_language = i18nResolvedLanguageForUiText;
  }
  if(i18nResolvedLanguageFromBrowser != null){
    i18n_resolved_language_from_browser = i18nResolvedLanguageFromBrowser;
  }
}

const instance = axios.create({
  timeout: 120000,
  transformResponse:
    // @ts-expect-error not an error
    axios.defaults.transformResponse?.concat(transformResponse),
  // @ts-expect-error not an error
  transformRequest: [transformRequest, ...axios.defaults.transformRequest],
});

const JsonApi: Api = {
  async get<T>(path: string, settings?: ApiRequestSettings | null): Promise<T> {
    return req<T>("GET", path, null, settings).then((d) => d.data);
  },
  async post<T>(
    path: string,
    input: Record<string, never> | null,
    settings?: ApiRequestSettings | null,
  ): Promise<T> {
    return req<T>("POST", path, input, settings).then((d) => d.data);
  },
  async put<T>(
    path: string,
    input: Record<string, never> | null,
    settings?: ApiRequestSettings | null,
  ): Promise<T> {
    return req<T>("PUT", path, input, settings).then((d) => d.data);
  },
  async del<T>(path: string, settings?: ApiRequestSettings | null): Promise<T> {
    return req<T>("DELETE", path, null, settings).then((d) => d.data);
  },
  async post_form<T>(
    path: string,
    input: Record<string, unknown>,
    settings?: ApiRequestSettings | null,
  ): Promise<T> {
    return req<T>("POST_FORM", path, input, settings).then((d) => d.data);
  },
  async fetchStream(path: string, body: unknown): Promise<Response> {
    const url = buildUrl(path);
    const xsrf = getXsrf();
    return fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        Accept: "text/event-stream",
        "Accept-Language": i18n_resolved_language,
        "Accept-Language-From-Browser": i18n_resolved_language_from_browser,
        ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
      },
      body: JSON.stringify(body),
    });
  },
};

// if we are in development mode (running vite as a dev server) and the backend server is also on local network,
//  we need to call the server using the subdomain (because of cross domain cookie does not work locally, except if https is used)
// (in production mode, we use the server base url)
function buildUrl(path: string): string {
  if (path.length > 0 && !path.startsWith("/")) {
    path = "/" + path;
  }
  // vite injects env vars as strings, so this must be compared against "true", not true
  const viteDevLocally = import.meta.env.VITE_DEV_LOCALLY === "true";
  // Sending the API to :9095 over plain http is for browsing the vite dev server directly,
  // where the page's own origin is :5372 and has no backend behind it.
  //
  // It must not happen on an https page. The browser blocks the request as mixed content
  // before it is even sent, so the app dies on its first call with a bare "Network Error".
  //
  // Swapping in the page's own protocol is not the fix: :9095 is wrong there too, because a
  // tunnel serves 443 and nothing else, so that only trades the mixed-content block for a
  // connection timeout. An https page is by definition already behind something that fronts
  // the backend, which is exactly what same-origin handles.
  // See etc/livekit/TESTING_LOCALLY.md.
  if (viteDevLocally && document.location.protocol !== "https:") {
    return "http://" + document.location.hostname + ":9095" + path;
  }
  return document.location.protocol + "//" + document.location.host + path;
}

// see DoubleSubmitCsrfFilter.kt
function getXsrf(): string | null {
  return getCookie("__Host-XSRF-TOKEN") || getCookie("XSRF-TOKEN");
}

async function req<T>(
  method: "POST" | "GET" | "PUT" | "DELETE" | "POST_FORM",
  path: string,
  input: Record<string, unknown> | null,
  settings?: ApiRequestSettings | null,
): Promise<AxiosResponse<T>> {
  const url = buildUrl(path);
  const XSRF = getXsrf();

  const options: AxiosRequestConfig = {
    withCredentials: true,

    signal: settings?.abortsignal || undefined,
    timeout: settings?.timeout || 60000,
    method: method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json;charset=UTF-8",
      "X-XSRF-TOKEN": XSRF,
      "Accept-Language": i18n_resolved_language,
      "Accept-Language-From-Browser": i18n_resolved_language_from_browser,
    },
  };

  if (method == "GET") {
    return instance.get(url, options);
  } else if (method == "POST") {
    return instance.post(url, input, options);
  } else if (method == "PUT") {
    return instance.put(url, input, options);
  } else if (method == "POST_FORM") {
    const formData = new FormData();
    if (input != null) {
      Object.keys(input).forEach((key) => {
        const wal: unknown = input[key];
        if (Array.isArray(wal)) {
          (wal as Array<never>).forEach((v) => {
            formData.append(key, v);
          });
        } else {
          const isObject =
            typeof wal === "object" &&
            wal !== null &&
            !(wal instanceof File) &&
            !(wal instanceof Blob);
          if (isObject) {
            formData.append(key, JSON.stringify(wal));
          } else {
            // @ts-expect-error ... Dont know
            formData.append(key, wal);
          }
        }
      });
    }
    // @ts-expect-error (headers are set, so OK)
    options.headers["Content-Type"] = "multipart/form-data";
    return instance.post(url, formData, options);
  } else {
    return instance.delete(url, options);
  }
}

export default JsonApi;

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(^|;\\s*)" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[2]) : null;
}
